import { Modal } from './Modal.js';
import { objectById, questConditions, questRewards, resourceLabel } from './schema.js';
import type { AssetLoader } from './AssetLoader.js';
import type { Economy } from './Economy.js';
import type { Progression } from './progression.js';
import type { GameConfig, Placement, Quest, QuestReward } from '../types.js';

interface QuestWindowOptions {
    config: GameConfig;
    assetLoader: AssetLoader;
    progression: Progression;
    economy: Economy;
    onClaim?: ((quest: Quest) => void) | null;
}

export class QuestWindow {
    private readonly config: GameConfig;
    private readonly assetLoader: AssetLoader;
    private readonly progression: Progression;
    private readonly economy: Economy;
    private readonly onClaim: ((quest: Quest) => void) | null;
    private readonly modal: Modal;
    private readonly list: HTMLElement;
    private placements: Placement[] = [];

    constructor({ config, assetLoader, progression, economy, onClaim = null }: QuestWindowOptions) {
        this.config = config;
        this.assetLoader = assetLoader;
        this.progression = progression;
        this.economy = economy;
        this.onClaim = onClaim;

        this.modal = new Modal({ title: 'QUESTS', variant: 'quest' });
        this.list = document.createElement('div');
        this.list.className = 'quest-list';
        this.modal.body.appendChild(this.list);
    }

    open(placements: Placement[]): void {
        this.placements = placements;
        this.render();
        this.modal.open();
    }

    close(): void {
        this.modal.close();
    }

    toggle(placements: Placement[]): void {
        if (this.modal.isOpen) this.close();
        else this.open(placements);
    }

    private render(): void {
        this.list.innerHTML = '';
        const status = this.progression.questStatus(this.placements, this.economy);

        if (!status.length) {
            const empty = document.createElement('p');
            empty.className = 'quest-empty';
            empty.textContent = 'This game has no quests.';
            this.list.appendChild(empty);
            return;
        }

        status.forEach(({ quest, satisfied, claimed }) => {
            this.list.appendChild(this.renderQuest(quest, satisfied, claimed));
        });
    }

    private renderQuest(quest: Quest, satisfied: boolean, claimed: boolean): HTMLElement {
        const row = document.createElement('div');
        row.className = 'quest';
        if (claimed) row.classList.add('is-claimed');

        const head = document.createElement('div');
        head.className = 'quest__head';

        const tick = document.createElement('span');
        tick.className = satisfied ? 'quest__tick is-done' : 'quest__tick';
        tick.textContent = satisfied ? '☑' : '☐';
        tick.setAttribute('aria-hidden', 'true');
        head.appendChild(tick);

        const name = document.createElement('span');
        name.className = 'quest__name';
        name.textContent = quest.name || quest.id;
        head.appendChild(name);

        row.appendChild(head);

        if (quest.speaker) {
            row.appendChild(this.renderSpeaker(quest.speaker));
        }

        if (quest.message) {
            const message = document.createElement('p');
            message.className = 'quest__message';
            message.textContent = quest.message;
            row.appendChild(message);
        }

        const objective = document.createElement('p');
        objective.className = 'quest__objective';
        objective.textContent = this.describeGoal(quest);
        row.appendChild(objective);

        const footer = document.createElement('div');
        footer.className = 'quest__footer';

        questRewards(quest).forEach((granted) => {
            const reward = document.createElement('span');
            reward.className = 'quest__reward';
            reward.textContent = this.describeReward(granted);
            footer.appendChild(reward);
        });

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'quest__claim';
        button.textContent = claimed ? 'CLAIMED' : 'GET REWARD';
        button.disabled = claimed || !satisfied;
        button.addEventListener('click', () => {
            if (this.progression.claim(quest, this.economy, this.placements)) {
                this.onClaim?.(quest);
                this.render();
            }
        });
        footer.appendChild(button);

        row.appendChild(footer);
        return row;
    }

    private renderSpeaker(speaker: NonNullable<Quest['speaker']>): HTMLElement {
        const block = document.createElement('div');
        block.className = 'quest__speaker';

        if (speaker.portrait && this.assetLoader.getImage(speaker.portrait)) {
            const portrait = document.createElement('img');
            portrait.className = 'quest__portrait';
            portrait.src = this.assetLoader.resolve(speaker.portrait);
            portrait.alt = '';
            block.appendChild(portrait);
        }

        const identity = document.createElement('div');
        identity.className = 'quest__speaker-identity';

        const name = document.createElement('span');
        name.className = 'quest__speaker-name';
        name.textContent = speaker.name;
        identity.appendChild(name);

        if (speaker.role) {
            const role = document.createElement('span');
            role.className = 'quest__speaker-role';
            role.textContent = speaker.role;
            identity.appendChild(role);
        }

        block.appendChild(identity);
        return block;
    }

    private describeReward(reward: QuestReward): string {
        if (reward.type === 'resource') {
            return `${reward.quantity ?? 0} ${resourceLabel(this.config, reward.resource)}`;
        }

        if (reward.type === 'unlock') {
            const object = objectById(this.config, reward.object);
            return `Unlocks ${object?.label ?? reward.object}`;
        }

        const category = (this.config.categories ?? [])
            .find((c) => c.id === reward.category);
        return `+${reward.quantity ?? 1} ${category?.label ?? reward.category} slot`;
    }

    private describeGoal(quest: Quest): string {
        const parts = questConditions(quest).flatMap((condition) => {
            const count = condition.quantity ?? 1;
            const many = count > 1 ? `${count} ` : '';

            if (condition.type === 'building' && condition.object) {
                const object = objectById(this.config, condition.object);
                return [`Build: ${many}${object?.label ?? condition.object}`];
            }

            if (condition.type === 'resource' && condition.resource) {
                return [`Collect: ${count} ${resourceLabel(this.config, condition.resource)}`];
            }

            return [];
        });

        return parts.join(' · ');
    }
}
